import React, { useMemo } from 'react';
import cx from 'classnames';
import { StreamingStructuredButtonTreeNodeData } from '@mutiny-pkg/champaign-schemas/src/schemas/flexiblePages/flexiblePagesTreeNode';
import { hydrateRichTextVariables, renderRichTextToPlainString, normalizeStreamingRichText } from '../../richtext/ast';
import { transformToContainerQueries } from '../utils/transformBreakpointClasses';

type ButtonElementProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  commandfor?: string;
  command?: string;
  role?: string;
  ['data-triggers-dialog']?: string;
};

type StructuredButtonProps = StreamingStructuredButtonTreeNodeData & {
  id: string;
  children?: React.ReactNode;
  editorClassName?: string;
  overrideVariables?: Partial<Record<string, string>>;
  onDoubleClick?: (event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void;
  isEditor: boolean;
};

export const StructuredButton = React.forwardRef<HTMLButtonElement | HTMLAnchorElement, StructuredButtonProps>(
  (
    {
      className,
      editorClassName,
      id,
      children,
      disabled,
      ariaLabel,
      command,
      commandfor,
      role,
      href,
      openInNewTab,
      overrideVariables,
      onDoubleClick,
      isEditor,
      ...props
    },
    ref,
  ) => {
    const buttonProps: ButtonElementProps = useMemo(() => {
      const baseProps: ButtonElementProps = {
        ...props,
        command,
        commandfor,
        role,
      };

      if (command === 'show-modal' && commandfor) {
        baseProps['data-triggers-dialog'] = commandfor;
      }

      return baseProps;
    }, [props, command, commandfor, role]);

    const normalizedHref = useMemo(() => (href ? normalizeStreamingRichText(href) : undefined), [href]);

    const hydratedHref = useMemo(() => {
      if (!normalizedHref) return undefined;
      return typeof normalizedHref !== 'string'
        ? hydrateRichTextVariables(normalizedHref, overrideVariables || {}, isEditor)
        : normalizedHref;
    }, [normalizedHref, overrideVariables, isEditor]);

    if (!href || (command && commandfor) || isEditor) {
      return (
        <button
          id={id}
          type="button"
          ref={ref as React.Ref<HTMLButtonElement>}
          className={cx(transformToContainerQueries(className, { isEditor }), editorClassName)}
          disabled={disabled}
          aria-label={ariaLabel}
          onDoubleClick={onDoubleClick}
          {...buttonProps}
        >
          {children}
        </button>
      );
    }

    const anchorProps = {
      ...props,
      href: hydratedHref ? renderRichTextToPlainString(hydratedHref) : undefined,
      target: openInNewTab ? '_blank' : undefined,
      rel: openInNewTab ? 'noopener noreferrer' : undefined,
    };

    return (
      <a
        id={id}
        ref={ref as React.Ref<HTMLAnchorElement>}
        className={cx(transformToContainerQueries(className, { isEditor }), editorClassName)}
        aria-label={ariaLabel}
        onDoubleClick={onDoubleClick}
        {...anchorProps}
      >
        {children}
      </a>
    );
  },
);

StructuredButton.displayName = 'StructuredButton';
